import { Stack, Checkbox, RadioGroup, RadioItem } from 'braid-design-system';
import type React from 'react';

import { type AvailableAnswer, SelectionType } from '../../sdk';
import { AnswerRange, type AnswerRangeType } from '../AnswerRange/AnswerRange';

export type AnswerChangeHandler = (
  answer: ToggleAnswer | ToggleRangeAnswer,
) => void;

type ToggleAnswer = {
  questionId: string;
  answerId: string;
  isSelected: boolean;
};

type ToggleRangeAnswer = {
  questionId: string;
  answerId: string;
  type: AnswerRangeType;
};

interface AnswerListProps {
  answers: AvailableAnswer[];
  questionId: string;
  selectionType: SelectionType;
  toggleAnswerSelection: AnswerChangeHandler;
}

type Handlers = {
  [key in SelectionType]: React.FunctionComponent<AnswerListProps>;
};

const CheckboxSelection = ({
  answers,
  questionId,
  toggleAnswerSelection,
}: AnswerListProps) => (
  <Stack space="small">
    {answers.map((answer) => (
      <Checkbox
        key={answer.id}
        id={answer.id}
        size="small"
        checked={answer.preferred}
        label={answer.text}
        onChange={() =>
          toggleAnswerSelection({
            questionId,
            answerId: answer.id,
            isSelected: !answer.preferred,
          })
        }
        message={false}
        reserveMessageSpace={false}
      />
    ))}
  </Stack>
);

const RadioSelection = ({
  answers,
  questionId,
  toggleAnswerSelection,
}: AnswerListProps) => (
  <RadioGroup
    id={questionId}
    aria-label={questionId}
    value={answers.filter((a) => a.preferred)[0]?.id}
    onChange={({ currentTarget: { value } }) => {
      toggleAnswerSelection({
        questionId,
        answerId: value,
        isSelected: !answers.find((a) => a.id === value)?.preferred,
      });
    }}
    size="small"
  >
    {answers.map((answer) => (
      <RadioItem key={answer.id} label={answer.text} value={answer.id} />
    ))}
  </RadioGroup>
);

const RangeSelection = ({
  answers,
  questionId,
  toggleAnswerSelection,
}: AnswerListProps) => (
  <AnswerRange
    questionId={questionId}
    answers={answers}
    onChange={(answerId, type) =>
      toggleAnswerSelection({
        questionId,
        answerId,
        type,
      })
    }
  />
);

const RegisteredHandlers: Handlers = {
  [SelectionType.Radio]: RadioSelection,
  [SelectionType.Checkbox]: CheckboxSelection,
  [SelectionType.Range]: RangeSelection,
  [SelectionType.FreeText]: () => null,
};

const AnswerList: React.FunctionComponent<AnswerListProps> = (props) =>
  RegisteredHandlers[props.selectionType](props);

export default AnswerList;
